import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { useEffect, useMemo } from 'react';
import { useAdminContext } from '../AdminContext';

export default function CsvTab() {
  const {
    selectedFile,
    setSelectedFile,
    isUploading,
    handleAppendCsvUpload,
    selectedHistoryFiles,
    setSelectedHistoryFiles,
    isImportingHistory,
    handleImportHistory,
    selectedGenusCsvFile,
    setSelectedGenusCsvFile,
    isImportingGenusChanges,
    handleImportGenusChanges,
    selectedFadingCsvFile,
    setSelectedFadingCsvFile,
    isImportingFading,
    handleImportFading,
    handleSyncMasterSheetFromImages,
    isSyncingMasterSheet,
    fadedAssetsClassInput,
    setFadedAssetsClassInput,
    isGeneratingFadedAssets,
    handleGenerateFadedAssetsForClass,
    fadeGenusInput,
    setFadeGenusInput,
    fadeGenusIncludeBandwagons,
    setFadeGenusIncludeBandwagons,
    isFadingGenus,
    fadeGenusPreview,
    handleFadeGenus,
    masterSheetData,
    selectedGenus,
  } = useAdminContext();

  const genusOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of masterSheetData || []) {
      const n = String(row?.genus_name || '').trim();
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [masterSheetData]);

  useEffect(() => {
    if (selectedGenus) setFadeGenusInput(selectedGenus);
  }, [selectedGenus, setFadeGenusInput]);

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Import Flair from CSV
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload a CSV to append rows (merge into existing data). Full replace and day rollback are not available from the UI.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="csv-upload-input"
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setSelectedFile(file);
            }}
          />
          <label htmlFor="csv-upload-input">
            <Button variant="outlined" component="span" startIcon={<UploadIcon />} disabled={isUploading} sx={{ mr: 2 }}>
              Select CSV File
            </Button>
          </label>
          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<UploadIcon />}
            onClick={handleAppendCsvUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Append CSV'}
          </Button>
        </Box>
        {isUploading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Uploading and processing CSV file...
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Import Flair History
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload a folder of CSV files with dates to import historical flair data. Each CSV should have a date in its
          filename (YYYY-MM-DD) or will use file modification date.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="history-csv-upload-input"
            type="file"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) setSelectedHistoryFiles(files);
            }}
          />
          <label htmlFor="history-csv-upload-input">
            <Button variant="outlined" component="span" startIcon={<UploadIcon />} disabled={isImportingHistory} sx={{ mr: 2 }}>
              Select CSV Files (Multiple)
            </Button>
          </label>
          {selectedHistoryFiles.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Selected: {selectedHistoryFiles.length} file(s)
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          color="info"
          startIcon={<UploadIcon />}
          onClick={handleImportHistory}
          disabled={selectedHistoryFiles.length === 0 || isImportingHistory}
        >
          {isImportingHistory ? 'Importing...' : 'Import History'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Import Bulk Genus Changes
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload a CSV file to bulk change flairs to new genuses. Format: Column 1 = Flair Name or Flair Abbreviated (e.g., "Shorter", "Arkansas-Fort Smith"), Column 2 = new genus name (e.g., "Conference Carolinas", "Mid-America IAA"). No headers required.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="genus-csv-upload-input"
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setSelectedGenusCsvFile(file);
            }}
          />
          <label htmlFor="genus-csv-upload-input">
            <Button variant="outlined" component="span" startIcon={<UploadIcon />} disabled={isImportingGenusChanges} sx={{ mr: 2 }}>
              Select CSV File
            </Button>
          </label>
          {selectedGenusCsvFile && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Selected: {selectedGenusCsvFile.name}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          color="info"
          startIcon={<UploadIcon />}
          onClick={handleImportGenusChanges}
          disabled={!selectedGenusCsvFile || isImportingGenusChanges}
        >
          {isImportingGenusChanges ? 'Importing...' : 'Import Bulk Genus Changes'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Import Fading
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload a CSV file to bulk fade or unfade flairs. Format: Column 1 = Image Name (not source), Column 2 = Fade or Unfade. Example: Duke Blue Devils,Fade or Campbell Camels,Unfade. No-op if already in target state.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <input
            accept=".csv"
            style={{ display: 'none' }}
            id="fading-csv-upload-input"
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setSelectedFadingCsvFile(file);
            }}
          />
          <label htmlFor="fading-csv-upload-input">
            <Button variant="outlined" component="span" startIcon={<UploadIcon />} disabled={isImportingFading} sx={{ mr: 2 }}>
              Select CSV File
            </Button>
          </label>
          {selectedFadingCsvFile && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Selected: {selectedFadingCsvFile.name}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="info"
            startIcon={<UploadIcon />}
            onClick={handleImportFading}
            disabled={!selectedFadingCsvFile || isImportingFading}
          >
            {isImportingFading ? 'Importing...' : 'Import Fading'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleSyncMasterSheetFromImages}
            disabled={isSyncingMasterSheet}
          >
            {isSyncingMasterSheet ? 'Syncing...' : 'Reconstruct Master Sheet from Images'}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Use &quot;Reconstruct Master Sheet from Images&quot; to sync master_sheet with the image table: remove rows for inactive images, remove duplicates (by image_id), and add missing rows for active images. Preserves alternates (same flair, different image_name). If alternates were removed by a previous import, run this to restore them.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Generate faded image assets (by class)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          For every active (non-faded) flair in the selected class, creates a desaturated/halftone PNG, uploads
          orig + all standard sizes to the CDN as <code>{'{stem}faded'}</code>, and inserts a matching{' '}
          <strong>inactive</strong> image row (<code>image_source</code> ending in <code>faded</code>,{' '}
          <code>image_description</code> like &quot;Team Name faded&quot;). Unfaded images stay active so
          nothing changes on Reddit until you use <strong>Import Fading</strong> with a CSV.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
          <TextField
            label="Class name"
            placeholder="e.g. D1"
            value={fadedAssetsClassInput}
            onChange={(e) => setFadedAssetsClassInput(e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
            disabled={isGeneratingFadedAssets}
          />
          <Button
            variant="outlined"
            onClick={() => handleGenerateFadedAssetsForClass(true)}
            disabled={isGeneratingFadedAssets || isImportingFading}
          >
            {isGeneratingFadedAssets ? <CircularProgress size={20} /> : 'Dry run'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleGenerateFadedAssetsForClass(false)}
            disabled={isGeneratingFadedAssets || isImportingFading}
          >
            {isGeneratingFadedAssets ? 'Working…' : 'Generate faded assets'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Fade or unfade a genus
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a team (genus). This hits <strong>every image in that genus</strong> — numbered alts, City Connect,
          Pride, and so on — not just the default logo. Missing faded art is generated automatically on Fade.
          New Reddit emojis are pushed after a real fade/unfade. Old Reddit still needs a spritesheet push from
          the Spritesheets tab.
        </Typography>
        <Autocomplete
          options={genusOptions}
          inputValue={fadeGenusInput}
          onInputChange={(_e, value) => setFadeGenusInput(value)}
          freeSolo
          disabled={isFadingGenus}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Genus (team)"
              placeholder="e.g. San Francisco Giants"
              size="small"
              helperText={
                genusOptions.length
                  ? `${genusOptions.length} genera on this sheet. Typing filters the list.`
                  : 'Load the flair sheet first so genus names appear here.'
              }
            />
          )}
          sx={{ maxWidth: 420, mb: 1 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={fadeGenusIncludeBandwagons}
              onChange={(e) => setFadeGenusIncludeBandwagons(e.target.checked)}
              disabled={isFadingGenus}
            />
          }
          label="Also include matching bandwagon (e.g. Giants Bandwagon)"
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => handleFadeGenus('fade', true)}
            disabled={isFadingGenus || isGeneratingFadedAssets || !fadeGenusInput?.trim()}
          >
            {isFadingGenus ? <CircularProgress size={20} /> : 'Preview'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleFadeGenus('fade', false)}
            disabled={isFadingGenus || isGeneratingFadedAssets || !fadeGenusInput?.trim()}
          >
            Fade genus
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleFadeGenus('unfade', false)}
            disabled={isFadingGenus || isGeneratingFadedAssets || !fadeGenusInput?.trim()}
          >
            Unfade genus
          </Button>
        </Box>
        {fadeGenusPreview && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {fadeGenusPreview.count} image{fadeGenusPreview.count !== 1 ? 's' : ''} in “{fadeGenusPreview.genusName}”
            </Typography>
            <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
              {(fadeGenusPreview.images || [])
                .map(
                  (img: { image_name: string; bandwagon?: boolean; currently_faded?: boolean }) =>
                    `• ${img.image_name}${img.bandwagon ? ' (bandwagon)' : ''}${
                      img.currently_faded ? ' — already faded' : ''
                    }`
                )
                .join('\n')}
            </Typography>
          </Alert>
        )}
      </Paper>
    </>
  );
}
